import { buttonStyle } from 'components/buttons/buttonStyle';
import { Button } from 'components/ui/button';
import EmploymentModal from 'components/modals/employment/EmploymentModal';
import { ModalActions } from 'components/modals/useModal';
import cloneDeep from 'lodash/cloneDeep';
import { Control, UseFormSetValue, useWatch } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { EmployeeForm, EmploymentForm } from 'types/employees';
import { NamedEntityMinDto } from 'types/other';
import { formatDateStringInput } from 'utils/formatDateStringInput';
import { calculateInitialValidFrom, findSmallestRecordId, sortEmploymentHistoryByValidFrom } from '../util';

export enum Position {
    SECTION_SIDE,
    HISTORY_SIDE,
}

interface Props {
    position: Position;
    control: Control<EmployeeForm>;
    setValue: UseFormSetValue<EmployeeForm>;
    jobRoleList: NamedEntityMinDto[];
    jobPositionList: NamedEntityMinDto[];
    modalActions: ModalActions;
}

const defaultInitialData: EmploymentForm = {
    recordId: 0,
    jobRole: null,
    jobPosition: null,
    office: null,
    coreTeam: null,
    validFrom: formatDateStringInput(new Date()),
    validTo: null,
    error: false,
};

const AddEmploymentButton = ({ control, setValue, position, jobRoleList, jobPositionList, modalActions }: Props) => {
    const employmentHistory = useWatch({ control, name: `employmentHistory` });

    const shouldBeDisplayed =
        position === Position.SECTION_SIDE ? employmentHistory.length !== 0 : employmentHistory.length === 0;

    if (!shouldBeDisplayed) {
        return null;
    }

    const addEmployment = (newEmployment: EmploymentForm) => {
        const employmentHistoryState = cloneDeep(employmentHistory ?? []);
        sortEmploymentHistoryByValidFrom(employmentHistoryState);
        const lastRecordId = findSmallestRecordId(employmentHistoryState);
        newEmployment.recordId = lastRecordId - 1;
        employmentHistoryState.push(newEmployment);
        setValue('employmentHistory', employmentHistoryState);
    };

    const openEmploymentModal = () =>
        modalActions.openModal(
            <EmploymentModal
                onClose={modalActions.closeModal}
                onSave={addEmployment}
                initialData={{ ...defaultInitialData, validFrom: calculateInitialValidFrom(employmentHistory) }}
                titleId={'employees.employment.form.add-title'}
                jobRoleList={jobRoleList}
                jobPositionList={jobPositionList}
            />
        );

    const variant = position === Position.SECTION_SIDE ? 'outlined' : 'contained';
    const color = position === Position.SECTION_SIDE ? 'primary' : 'secondary';
    return (
        <Button {...buttonStyle({ variant, color, size: 'small' }, 'ml-3')} onClick={openEmploymentModal}>
            <FormattedMessage id="employees.employment.add" />
        </Button>
    );
};

export default AddEmploymentButton;
